import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Bell,
  Users,
  TrendingUp,
  Database,
  Key,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import NotificationPanel from "../components/NotificationPanel";
import ChangePasswordDialog from "../components/ChangePasswordDialog";

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
    fetchNotifications();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get("/dashboard/stats");
      setStats(response.data);
    } catch (error) {
      toast.error("Gagal memuat statistik");
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axiosInstance.get("/notifications");
      const unread = response.data.filter((n) => !n.is_read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error("Failed to fetch notifications");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f0f10] via-[#1a1a1b] to-[#0f0f10]" data-testid="dashboard-page">
      {/* Top bar */}
      <header className="bg-[#1a1a1b]/80 backdrop-blur-xl border-b border-red-900/30 sticky top-0 z-20">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-semibold text-red-50 pl-12 lg:pl-0">Dashboard</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-gray-300 hover:text-white hover:bg-red-900/20 rounded-lg transition-colors"
                data-testid="notification-button"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold"
                    data-testid="notification-badge"
                  >
                    {unreadCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowChangePassword(true)}
                className="p-2 text-gray-300 hover:text-white hover:bg-red-900/20 rounded-lg transition-colors"
                data-testid="change-password-button"
                title="Ubah Password"
              >
                <Key size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {showNotifications && (
        <NotificationPanel
          onClose={() => setShowNotifications(false)}
          onUpdate={fetchNotifications}
        />
      )}

      <main className="px-4 sm:px-6 lg:px-8 py-8 max-w-7xl mx-auto">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-red-50 mb-2">Selamat Datang, {user?.name}!</h2>
          <p className="text-gray-400">Berikut adalah ringkasan data member Anda</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-red-600 border-t-transparent"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30 hover:border-red-800/50 transition-all" data-testid="stat-total-members">
                <CardHeader className="pb-2">
                  <CardDescription className="text-gray-400">Total Member</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-bold text-red-50">{stats?.total_members || 0}</div>
                    <Users className="w-8 h-8 text-red-600" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30 hover:border-red-800/50 transition-all" data-testid="stat-total-ndp">
                <CardHeader className="pb-2">
                  <CardDescription className="text-gray-400">Member NDP</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-bold text-green-400">{stats?.total_ndp || 0}</div>
                    <TrendingUp className="w-8 h-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30 hover:border-red-800/50 transition-all" data-testid="stat-total-regis">
                <CardHeader className="pb-2">
                  <CardDescription className="text-gray-400">Member Regis</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-bold text-yellow-400">{stats?.total_regis || 0}</div>
                    <Users className="w-8 h-8 text-yellow-600" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30 hover:border-red-800/50 transition-all" data-testid="stat-in-review">
                <CardHeader className="pb-2">
                  <CardDescription className="text-gray-400">In Review</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-bold text-blue-400">{stats?.total_in_review || 0}</div>
                    <Clock className="w-8 h-8 text-blue-600" />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">NDP belum di-submit</p>
                </CardContent>
              </Card>

              <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30 hover:border-red-800/50 transition-all" data-testid="stat-total-depo">
                <CardHeader className="pb-2">
                  <CardDescription className="text-gray-400">Total Depo</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-bold text-red-50">
                      Rp {(stats?.total_depo || 0).toLocaleString("id-ID")}
                    </div>
                    <Database className="w-8 h-8 text-red-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {user?.role !== "marketing" && stats?.recent_members && stats.recent_members.length > 0 && (
              <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30">
                <CardHeader>
                  <CardTitle className="text-red-50">Member Terbaru</CardTitle>
                  <CardDescription className="text-gray-400">5 member yang baru ditambahkan</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {stats.recent_members.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-4 bg-[#0f0f10] rounded-lg border border-red-900/20 hover:border-red-800/40 transition-colors"
                        data-testid={`recent-member-${member.id}`}
                      >
                        <div>
                          <div className="font-semibold text-white">{member.username}</div>
                          <div className="text-sm text-gray-400">
                            {member.marketing_name} • {member.website}
                          </div>
                        </div>
                        <div className="text-right">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              member.member_type === "ndp"
                                ? "bg-green-900/30 text-green-400 border border-green-700/50"
                                : "bg-yellow-900/30 text-yellow-400 border border-yellow-700/50"
                            }`}
                          >
                            {member.member_type.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>

      <ChangePasswordDialog open={showChangePassword} onOpenChange={setShowChangePassword} />
    </div>
  );
};

export default Dashboard;
